import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';

const checklistItems = [
  { id: 'qui', letter: 'Q', label: 'Qui', description: 'Qui appelle? Nom, lien avec la victime' },
  { id: 'quoi', letter: 'Q', label: 'Quoi', description: "Qu'est-ce qui se passe exactement?" },
  { id: 'quand', letter: 'Q', label: 'Quand', description: "Quand est-ce arrivé?" },
  { id: 'pourquoi', letter: 'P', label: 'Pourquoi', description: "Pourquoi appelez-vous? Contexte" },
  { id: 'ou', letter: 'O', label: 'Où', description: "Où êtes-vous? Adresse exacte" },
  { id: 'comment', letter: 'C', label: 'Comment', description: "Comment c'est arrivé?" },
  { id: 'blesses', letter: 'B', label: 'Blessés', description: "Y a-t-il des blessés? Combien?" },
  { id: 'armes', letter: 'A', label: 'Armes', description: "Y a-t-il des armes impliquées?" },
  { id: 'risques', letter: 'R', label: 'Risques', description: "Y a-t-il des risques supplémentaires?" },
];

const accentColors = {
  Q: '#2E6CF5',
  P: '#7C3AED',
  O: '#059669',
  C: '#D97706',
  B: '#EE2130',
  A: '#DC2626',
  R: '#B91C1C',
};

const DispatcherChecklist = ({ onOpen }) => {
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  useEffect(() => {
    if (expanded && !hasBeenOpened) {
      setHasBeenOpened(true);
      onOpen?.();
    }
  }, [expanded, hasBeenOpened, onOpen]);

  const toggle = (id) => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const reset = () => setChecked({});

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const total = checklistItems.length;
  const progress = (checkedCount / total) * 100;

  return (
    <Card className="glass border-0 mb-6" data-testid="dispatcher-checklist">
      <CardHeader
        className="cursor-pointer hover:bg-gray-50/50 transition-colors py-3 px-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center space-x-2">
            <ClipboardCheck className="w-4 h-4" style={{ color: '#002768' }} />
            <span style={{ color: '#002768' }}>3QPOC — BAR</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: checkedCount === total ? '#D1FAE5' : '#EFF6FF',
                    color: checkedCount === total ? '#065F46' : '#1E40AF'
                  }}>
              {checkedCount}/{total}
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
          <div
            className="h-1.5 rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              background: checkedCount === total
                ? 'linear-gradient(to right, #059669, #34D399)'
                : 'linear-gradient(to right, #002768, #2E6CF5)'
            }}
          />
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 px-4 pb-3">
          <ul className="space-y-1" data-testid="checklist-items">
            {checklistItems.map((item) => {
              const isChecked = !!checked[item.id];
              const color = accentColors[item.letter] || '#2E6CF5';
              return (
                <li
                  key={item.id}
                  data-testid={`checklist-item-${item.id}`}
                  onClick={() => toggle(item.id)}
                  className="flex items-start gap-2.5 py-1.5 px-2 rounded-lg cursor-pointer transition-all duration-200 group"
                  style={{
                    backgroundColor: isChecked ? '#F0FDF4' : 'transparent',
                  }}
                >
                  {/* Checkbox */}
                  <div
                    className="mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-all duration-200"
                    style={{
                      borderColor: isChecked ? '#059669' : '#CBD5E1',
                      backgroundColor: isChecked ? '#059669' : 'transparent',
                    }}
                  >
                    {isChecked && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>

                  {/* Letter badge */}
                  <span
                    className="text-xs font-bold w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      backgroundColor: isChecked ? '#E5E7EB' : `${color}15`,
                      color: isChecked ? '#9CA3AF' : color,
                    }}
                  >
                    {item.letter}
                  </span>

                  {/* Label + description */}
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-sm font-semibold transition-all duration-200"
                      style={{
                        color: isChecked ? '#9CA3AF' : '#1F2937',
                        textDecoration: isChecked ? 'line-through' : 'none',
                      }}
                    >
                      {item.label}
                    </span>
                    <p
                      className="text-xs leading-tight mt-0.5 transition-all duration-200"
                      style={{ color: isChecked ? '#D1D5DB' : '#6B7280' }}
                    >
                      {item.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Reset button */}
          {checkedCount > 0 && (
            <button
              data-testid="checklist-reset-btn"
              onClick={reset}
              className="mt-2 flex items-center space-x-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors mx-auto"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Réinitialiser</span>
            </button>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default DispatcherChecklist;
